/**
 * Per-zone plugin settings surfaced on the Integrations Settings page.
 *
 * Each plugin owns its own ``…Settings`` table (one row per Zone) with
 * a Telegram ``chat_id`` + ``thread_id`` pair used for that plugin's
 * outbound alerts. The endpoints are intentionally tiny — GET returns
 * (creating on demand) and PATCH updates.
 */
import { axiosInstance } from './api';

// ── Shared shape ─────────────────────────────────────────────────────────────

export interface PluginChatSettings {
  chat_id: string | null;
  thread_id: string | null;
}

export type PluginChatPatch = Partial<PluginChatSettings>;

// ── Periodic Reports (daily summary) ─────────────────────────────────────────

export interface PeriodicReportsSettingsDto {
  id: number;
  zone: number;
  daily_report_chat_id: string | null;
  daily_report_thread_id: string | null;
}

export const getPeriodicReportsSettings = (zoneId: string) =>
  axiosInstance.get<PeriodicReportsSettingsDto>(
    `/periodic_reports/settings/${zoneId}/`,
  );

export const patchPeriodicReportsSettings = (
  zoneId: string,
  payload: Partial<Pick<PeriodicReportsSettingsDto, 'daily_report_chat_id' | 'daily_report_thread_id'>>,
) =>
  axiosInstance.patch<PeriodicReportsSettingsDto>(
    `/periodic_reports/settings/${zoneId}/`,
    payload,
  );

// ── Checkout Control ─────────────────────────────────────────────────────────

export interface CheckoutControlSettingsDto {
  id: number;
  zone: number;
  checkout_control_chat_id: string | null;
  checkout_control_thread_id: string | null;
}

export const getCheckoutControlSettings = (zoneId: string) =>
  axiosInstance.get<CheckoutControlSettingsDto>(
    `/checkout_control/settings/${zoneId}/`,
  );

export const patchCheckoutControlSettings = (
  zoneId: string,
  payload: Partial<Pick<CheckoutControlSettingsDto, 'checkout_control_chat_id' | 'checkout_control_thread_id'>>,
) =>
  axiosInstance.patch<CheckoutControlSettingsDto>(
    `/checkout_control/settings/${zoneId}/`,
    payload,
  );

// ── Table Control ────────────────────────────────────────────────────────────

export interface TableControlSettingsDto {
  id: number;
  zone: number;
  table_control_chat_id: string | null;
  table_control_thread_id: string | null;
}

export const getTableControlSettings = (zoneId: string) =>
  axiosInstance.get<TableControlSettingsDto>(
    `/table_control/settings/${zoneId}/`,
  );

export const patchTableControlSettings = (
  zoneId: string,
  payload: Partial<Pick<TableControlSettingsDto, 'table_control_chat_id' | 'table_control_thread_id'>>,
) =>
  axiosInstance.patch<TableControlSettingsDto>(
    `/table_control/settings/${zoneId}/`,
    payload,
  );

// ── Ops Tasks reminders (already exposed via ops_tasks/reminders-settings/) ──

export interface ZoneRemindersSettingsDto {
  id: number;
  zone: number;
  deadline_pre_reminder_minutes: number;
  overdue_minutes: number;
  send_pre_deadline_message: boolean;
  send_deadline_message: boolean;
  send_overdue_message: boolean;
  ops_tasks_chat_id: string | null;
  ops_tasks_thread_id: string | null;
}

/**
 * The existing endpoint returns 204 (No Content) when settings haven't been
 * initialised — call ``initZoneRemindersSettings`` then. The Integrations
 * page handles the 204 by auto-initialising before the first PATCH.
 */
export const getZoneRemindersSettings = (zoneId: string) =>
  axiosInstance.get<ZoneRemindersSettingsDto>(
    `/ops_tasks/reminders-settings/${zoneId}/`,
  );

export const initZoneRemindersSettings = (zoneId: string) =>
  axiosInstance.post<ZoneRemindersSettingsDto>(
    `/ops_tasks/reminders-settings/${zoneId}/`,
  );

export const patchZoneRemindersSettings = (
  zoneId: string,
  payload: Partial<Pick<ZoneRemindersSettingsDto, 'ops_tasks_chat_id' | 'ops_tasks_thread_id'>>,
) =>
  axiosInstance.patch<ZoneRemindersSettingsDto>(
    `/ops_tasks/reminders-settings/${zoneId}/`,
    payload,
  );
